/**
 * Bond Mentor — Market Data
 *
 * BASELINE: Real market conditions as of 03-26-2026.
 * The "All Clear" scenario uses actual current market numbers.
 * "Early Warning" and "Danger Zone" are realistic stress scenarios
 * scaled from the actual baseline — not arbitrary numbers.
 *
 * When live data is connected via the proxy server, these scenarios
 * are only used in the Simulator (training mode). Live data replaces
 * them on the main Dashboard and other tabs.
 *
 * Sources: FRED, TreasuryDirect, CBOE, ICE BofA
 * Last calibrated: 03-26-2026
 */

// ============================================
// MARKET SCENARIOS
// ============================================

export const SCENARIOS = {
  /**
   * ALL CLEAR — Based on ACTUAL market conditions as of 03-26-2026.
   *
   * Current reality: Fed at 3.50-3.75% after 2025 easing cycle.
   * 10Y at ~4.39% (elevated due to U.S.-Iran conflict inflation fears).
   * 2Y at ~3.94%. Curve is positively sloped (+45 bps).
   * IG spreads tight at 87 bps. HY at 319 bps.
   * VIX elevated at ~27.6 due to geopolitical risk.
   * MOVE at ~97.6, reflecting bond market uncertainty.
   *
   * NOTE: Current conditions are actually mixed — the VIX at 27.6 and
   * MOVE at 97.6 would normally push toward YELLOW. But the yield curve
   * is healthy and credit is tight. For the "All Clear" demo, we use
   * actual numbers to show what the REAL market looks like right now.
   */
  ALL_CLEAR: {
    name: 'All Clear — Current Market (03-26-2026)',
    description: 'Based on actual market data. Fed at 3.50-3.75%, curve positive, credit tight. VIX elevated on geopolitical risk but credit fundamentals healthy.',
    subSignals: {
      yieldCurve: 10,         // Curve is healthy, +45bp 2s/10s
      creditSpreads: 8,       // IG 87bps, HY 319bps — very tight
      fedPosture: 20,         // Fed on hold after easing, slight tension
      vigilanteActivity: 15,  // Some concern about deficit but manageable
      volatility: 35,         // VIX at 27.6 is elevated (geopolitical)
      liquidity: 12,          // Liquidity is fine
      moneyFlows: 18,         // Some flight-to-safety flows from Iran conflict
      globalBonds: 22,        // Moderate global tension
    },
    keyMetrics: {
      tenYearYield: 4.39,
      twoYearYield: 3.94,
      spreadTwoTen: 0.45,
      fedFundsRate: 3.625,    // Midpoint of 3.50-3.75% range
      igSpread: 87,
      hySpread: 319,
      moveIndex: 97.59,
      vix: 27.63,
    },
    lastUpdated: new Date(2026, 2, 26),
  },

  /**
   * EARLY WARNING — Realistic stress scenario from current baseline.
   *
   * What would happen if: Iran conflict escalates, oil spikes,
   * inflation re-accelerates, Fed forced to pause/consider hiking,
   * curve starts flattening, credit begins to widen.
   */
  EARLY_WARNING: {
    name: 'Early Warning — Caution Emerging',
    description: 'Scenario: Geopolitical escalation drives inflation higher. Fed hawkish again. Curve flattening. Credit widening. Smart money repositioning.',
    subSignals: {
      yieldCurve: 42,
      creditSpreads: 45,
      fedPosture: 52,
      vigilanteActivity: 38,
      volatility: 48,
      liquidity: 32,
      moneyFlows: 35,
      globalBonds: 40,
    },
    keyMetrics: {
      tenYearYield: 4.72,
      twoYearYield: 4.65,
      spreadTwoTen: 0.07,
      fedFundsRate: 3.625,    // Fed hasn't moved yet but market fears hike
      igSpread: 135,
      hySpread: 445,
      moveIndex: 128,
      vix: 32.5,
    },
    lastUpdated: new Date(2026, 2, 26),
  },

  /**
   * DANGER ZONE — Full crisis scenario.
   *
   * What would happen if: Full-scale regional war, oil above $150,
   * inflation surging past 5%, Fed behind the curve, credit markets
   * seizing, foreign selling of Treasuries, liquidity crisis.
   */
  DANGER_ZONE: {
    name: 'Danger Zone — Deleverage Now',
    description: 'Scenario: Full crisis. Regional war escalation, inflation surging, curve inverted, credit blowing out, liquidity failing, global contagion.',
    subSignals: {
      yieldCurve: 80,
      creditSpreads: 82,
      fedPosture: 75,
      vigilanteActivity: 78,
      volatility: 85,
      liquidity: 72,
      moneyFlows: 68,
      globalBonds: 65,
    },
    keyMetrics: {
      tenYearYield: 5.35,
      twoYearYield: 5.70,
      spreadTwoTen: -0.35,
      fedFundsRate: 4.75,     // Fed forced to hike in crisis
      igSpread: 225,
      hySpread: 680,
      moveIndex: 172,
      vix: 42.5,
    },
    lastUpdated: new Date(2026, 2, 26),
  },
};

// ============================================
// HISTORICAL SIGNAL LOG (reflects real recent history)
// ============================================
export const SIGNAL_HISTORY = [
  { date: new Date(2026, 2, 26, 9, 30), signal: 'GREEN', score: 18, action: 'Tighten Stops' },
  { date: new Date(2026, 2, 25, 16, 0), signal: 'GREEN', score: 20, action: 'Tighten Stops' },
  { date: new Date(2026, 2, 24, 16, 0), signal: 'GREEN', score: 22, action: 'Tighten Stops' },
  { date: new Date(2026, 2, 21, 16, 0), signal: 'YELLOW', score: 32, action: 'Reduce to 75%' },
  { date: new Date(2026, 2, 20, 16, 0), signal: 'YELLOW', score: 35, action: 'Reduce to 75%' },
  { date: new Date(2026, 2, 19, 16, 0), signal: 'YELLOW', score: 38, action: 'Reduce to 75%' },
  { date: new Date(2026, 2, 18, 16, 0), signal: 'YELLOW', score: 34, action: 'Reduce to 75%' },
  { date: new Date(2026, 2, 14, 16, 0), signal: 'YELLOW', score: 28, action: 'Reduce to 75%' },
  { date: new Date(2026, 2, 13, 16, 0), signal: 'YELLOW', score: 25, action: 'Tighten Stops' },
  { date: new Date(2026, 2, 12, 16, 0), signal: 'GREEN', score: 22, action: 'Tighten Stops' },
];

// ============================================
// SUB-SIGNAL METADATA (for display)
// ============================================
export const SUB_SIGNAL_META = {
  yieldCurve: {
    label: 'Yield Curve',
    icon: 'TrendingUp',
    weight: '20%',
    description: 'Shape and direction of the Treasury yield curve — the #1 recession predictor.',
    dropAvailable: 2,
  },
  creditSpreads: {
    label: 'Credit Spreads',
    icon: 'BarChart3',
    weight: '20%',
    description: 'Gap between safe government bonds and riskier corporate bonds — the fear gauge.',
    dropAvailable: 4,
  },
  fedPosture: {
    label: 'Fed Watch',
    icon: 'Landmark',
    weight: '15%',
    description: 'Federal Reserve policy direction and whether bonds agree or disagree with the Fed.',
    dropAvailable: 2,
  },
  vigilanteActivity: {
    label: 'Bond Vigilantes',
    icon: 'Shield',
    weight: '15%',
    description: 'Activity level of large bond sellers punishing fiscal or monetary irresponsibility.',
    dropAvailable: 3,
  },
  volatility: {
    label: 'Volatility',
    icon: 'Activity',
    weight: '10%',
    description: 'Bond vs. stock volatility comparison — detects when stocks ignore bond market stress.',
    dropAvailable: 4,
  },
  liquidity: {
    label: 'Liquidity',
    icon: 'Droplets',
    weight: '10%',
    description: 'Health of financial system plumbing — repo markets, Treasury liquidity, money flows.',
    dropAvailable: 5,
  },
  moneyFlows: {
    label: 'Money Flows',
    icon: 'ArrowRightLeft',
    weight: '5%',
    description: 'Where institutional money is moving — into safety or into risk assets.',
    dropAvailable: 5,
  },
  globalBonds: {
    label: 'Global Bonds',
    icon: 'Globe',
    weight: '5%',
    description: 'Major international bond markets and whether foreign moves threaten U.S. markets.',
    dropAvailable: 6,
  },
};
